/**
 * workflowStatus.ts
 *
 * Pure mapping from a DD/BA job to the words shown in the Workspace Top_Bar:
 * the item title and the Status_Pill (stage in words, never colour alone).
 *
 * Requirements: 3.2, 3.6.
 */

import { JobStatus, JobType, StepStatus, type Job } from '../graphql/types';
import type { PillTone } from '../components/ui';
import type { WorkspaceMode } from './urlState';

export interface WorkflowStatusPill {
  label: string;
  tone: PillTone;
}

/** "21 of 24 loans" for DD. BA works on a single loan, so no count is shown. */
function loanProgress(job: Pick<Job, 'jobType' | 'loanCount' | 'processedLoanCount'>): string {
  if (job.jobType !== JobType.DD || !(job.loanCount > 0)) return '';
  const done = Math.min(Math.max(job.processedLoanCount ?? 0, 0), job.loanCount);
  return ` · ${done} of ${job.loanCount} ${job.loanCount === 1 ? 'loan' : 'loans'}`;
}

/**
 * Stage in words for the Status_Pill, for example "Analysis · 21 of 24 loans"
 * or "Data review".
 */
export function getWorkflowStatusPill(
  job: Pick<
    Job,
    'jobType' | 'status' | 'loanCount' | 'processedLoanCount' | 'documentProcessingStatus'
  >
): WorkflowStatusPill {
  switch (job.status) {
    case JobStatus.DOCUMENT_PROCESSING:
      if (job.documentProcessingStatus === StepStatus.IN_PROGRESS) {
        return { label: `Extracting${loanProgress(job)}`, tone: 'selected' };
      }
      return { label: 'Intake', tone: 'neutral' };
    case JobStatus.DOCUMENT_VERIFICATION:
      return { label: 'Data review', tone: 'warning' };
    case JobStatus.INVESTOR_GUIDELINE_REVIEW:
    case JobStatus.UNDERWRITING_GUIDELINE_REVIEW:
      return { label: `Analysis${loanProgress(job)}`, tone: 'selected' };
    case JobStatus.COMPLETED:
      return { label: 'Completed', tone: 'success' };
    case JobStatus.FAILED:
      return { label: 'Failed', tone: 'danger' };
    case JobStatus.ARCHIVED:
      return { label: 'Archived', tone: 'neutral' };
    default:
      return { label: String(job.status).replace(/_/g, ' ').toLowerCase(), tone: 'neutral' };
  }
}

/**
 * Counts a Mode body or Rail can report to the Workspace so the Top_Bar can
 * say more than the stage. Every field is optional: an unknown count falls
 * back to the stage wording from `getWorkflowStatusPill`.
 */
export interface WorkflowStatusContext {
  /** DD Intake: files added across all loans. */
  fileCount?: number;
  /** BA Intake: required documents received, and how many are required. */
  receivedDocuments?: number;
  requiredDocuments?: number;
  /** DD field review: fields (or documents) confirmed, and how many there are. */
  confirmedFields?: number;
  totalFields?: number;
  /** BA guideline review: findings still open. */
  openFindings?: number;
  /** BA title: borrower and loan number when the job itself does not carry them. */
  borrowerName?: string | null;
  loanNumber?: string | null;
}

type StatusJob = Pick<
  Job,
  | 'jobType'
  | 'status'
  | 'loanCount'
  | 'processedLoanCount'
  | 'documentProcessingStatus'
  | 'verifiedDocumentCount'
  | 'totalDocumentCount'
>;

const isCount = (n: number | null | undefined): n is number =>
  typeof n === 'number' && Number.isFinite(n) && n >= 0;

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/**
 * Status_Pill wording per Mode, as in the prototype:
 * - DD: "Intake · 96 files", "Field review · 4 of 7 confirmed",
 *   "Analysis · 21 of 24 loans"
 * - BA: "Intake · 4 of 5 docs", "Field review", "Guideline review · 2 open"
 *
 * Counts come from `context` first, then from the job. When a count is not
 * known the stage wording of `getWorkflowStatusPill` is kept. Finished and
 * failed jobs keep their terminal wording in every Mode.
 */
export function getWorkflowModeStatusPill(
  job: StatusJob,
  mode: WorkspaceMode,
  context: WorkflowStatusContext = {}
): WorkflowStatusPill {
  const stage = getWorkflowStatusPill(job);
  if (
    job.status === JobStatus.COMPLETED ||
    job.status === JobStatus.FAILED ||
    job.status === JobStatus.ARCHIVED
  ) {
    return stage;
  }
  const isDD = job.jobType === JobType.DD;

  if (mode === 'intake') {
    if (isDD) {
      return isCount(context.fileCount)
        ? {
            label: `Intake · ${context.fileCount} ${plural(context.fileCount, 'file', 'files')}`,
            tone: 'neutral',
          }
        : stage;
    }
    return isCount(context.receivedDocuments) && isCount(context.requiredDocuments)
      ? {
          label: `Intake · ${context.receivedDocuments} of ${context.requiredDocuments} docs`,
          tone: 'neutral',
        }
      : stage;
  }

  if (mode === 'extract') {
    if (!isDD) return { label: 'Field review', tone: 'warning' };
    const confirmed = isCount(context.confirmedFields)
      ? context.confirmedFields
      : job.verifiedDocumentCount;
    const total = isCount(context.totalFields) ? context.totalFields : job.totalDocumentCount;
    return isCount(confirmed) && isCount(total) && total > 0
      ? { label: `Field review · ${Math.min(confirmed, total)} of ${total} confirmed`, tone: 'warning' }
      : stage;
  }

  if (mode === 'analyze') {
    if (isDD) {
      return job.loanCount > 0
        ? { label: `Analysis${loanProgress(job)}`, tone: 'selected' }
        : stage;
    }
    return isCount(context.openFindings)
      ? { label: `Guideline review · ${context.openFindings} open`, tone: 'selected' }
      : stage;
  }

  return stage;
}

/** Status_Pill label of a draft: DD has no loans until the tape or list exists. */
export function getDraftStatusLabel(feature: JobType): string {
  return feature === JobType.DD ? 'Draft · no loans yet' : 'Draft';
}

/**
 * Top_Bar title. BA reads "Okonkwo, Michael · LN-4471903" (borrower · loan
 * number) when either is known; DD uses the job name.
 */
export function getWorkflowTitle(
  job: Pick<Job, 'jobType' | 'name' | 'borrowerName' | 'loanNumber'> | null | undefined,
  fallback: string,
  context: Pick<WorkflowStatusContext, 'borrowerName' | 'loanNumber'> = {}
): string {
  if (!job) return fallback;
  if (job.jobType === JobType.BA) {
    const borrower = (context.borrowerName ?? job.borrowerName ?? '').trim();
    const loan = (context.loanNumber ?? job.loanNumber ?? '').trim();
    const parts = [borrower, loan].filter(Boolean);
    if (parts.length > 0) return parts.join(' · ');
  }
  return job.name?.trim() || fallback;
}
